import { trpc } from '@/lib/trpc'
import { getIdToken } from 'firebase/auth'
import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import logoImage from '../assets/acts-full-logo.svg'
import googleIcon from '../assets/google-96.svg'
import LanguageSwitcher from '../components/LanguageSwitcher'
import { auth, provider, signInWithPopup } from '../services/firebase'
import { useAuthStore, getToken, removeToken, trpcClient } from '@repo/api-client'
import { TRPCClientError } from '@trpc/client'
import { Backdrop, CircularProgress, Typography } from '@mui/material'

const Login = () => {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const queryParams = new URLSearchParams(window.location.search)
  const redirectUrl = decodeURIComponent(queryParams.get('redirect_uri') ?? '')

  const user = useAuthStore((state) => state.user)
  const login = useAuthStore((state) => state.login)
  const logout = useAuthStore((state) => state.logout)

  useEffect(() => {
    const currentParams = new URLSearchParams(window.location.search)
    const isLogout =
      currentParams.get('action') === 'logout' ||
      currentParams.get('logout') === 'true' ||
      window.location.pathname === '/logout'

    if (isLogout) {
      logout()
      removeToken()
      auth.signOut().catch(() => {})
      const url = new URL(window.location.href)
      url.searchParams.delete('action')
      url.searchParams.delete('logout')
      const cleanPath = url.pathname === '/logout' ? '/' : url.pathname
      window.history.replaceState({}, '', cleanPath + (url.search ? url.search : ''))
      return
    }

    const validCookieToken = getToken()
    const reason = currentParams.get('reason')

    // If redirected due to expired/invalid token or explicit logout, clear stale state and prompt login
    if (reason === 'token_expired' || reason === 'session_expired' || reason === 'invalid_token') {
      if (user || validCookieToken) {
        logout()
        removeToken()
        auth.signOut().catch(() => {})
      }
      return
    }

    if (!validCookieToken) {
      // If shared session cookie is absent, clear any stale user/token
      if (user) {
        logout()
        removeToken()
        auth.signOut().catch(() => {})
      }
      return
    }

    if (user && validCookieToken) {
      // Verify token validity with backend before redirecting back to prevent loops
      let isSubscribed = true
      trpcClient.auth.verifyJWT
        .query({ token: validCookieToken })
        .then((result) => {
          if (!isSubscribed) return
          if (result?.userId) {
            if (redirectUrl) {
              try {
                const url = new URL(redirectUrl)
                url.searchParams.set('token', validCookieToken)
                url.searchParams.set('lng', i18n.language)
                window.location.href = url.toString()
              } catch {
                navigate('/profile')
              }
            } else {
              navigate('/profile')
            }
          } else {
            logout()
            removeToken()
            auth.signOut().catch(() => {})
          }
        })
        .catch((err) => {
          if (!isSubscribed) return
          console.warn('[Login] Existing token validation failed:', err)
          logout()
          removeToken()
          auth.signOut().catch(() => {})
        })

      return () => {
        isSubscribed = false
      }
    }
  }, [user, redirectUrl, navigate, i18n.language, logout])

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const [error, setError] = useState('')

  // Tab state for standard login
  const [activeTab, setActiveTab] = useState<'password' | 'mobile'>('password')
  const [mobilePhone, setMobilePhone] = useState('')
  const [mobileOtp, setMobileOtp] = useState('')
  const [mobileOtpSent, setMobileOtpSent] = useState(false)
  const [mobileRef, setMobileRef] = useState('')

  // Expired token OTP state
  const [expiredToken, setExpiredToken] = useState<string | null>(null)
  const [expiredOtp, setExpiredOtp] = useState('')
  const [expiredOtpSent, setExpiredOtpSent] = useState(false)
  const [expiredMaskedPhone, setExpiredMaskedPhone] = useState('')
  const [expiredRef, setExpiredRef] = useState('')

  // tRPC mutation hooks
  const loginWithPasswordMutation = trpc.auth.loginWithPassword.useMutation()
  const loginWithFirebaseMutation = trpc.auth.loginWithFirebase.useMutation()
  const requestExpiredTokenOtpMutation = trpc.auth.requestExpiredTokenOtp.useMutation()
  const verifyExpiredTokenOtpMutation = trpc.auth.verifyExpiredTokenOtp.useMutation()
  const requestMobileLoginOtpMutation = trpc.auth.requestMobileLoginOtp.useMutation()
  const verifyMobileLoginOtpMutation = trpc.auth.verifyMobileLoginOtp.useMutation()

  // Dev Mock SMS
  const { data: mockSmsData } = trpc.user.getRecentMockSms.useQuery(
    { phone: mobilePhone || undefined, ref: mobileRef || undefined },
    { enabled: (activeTab === 'mobile' || !!expiredToken) && (import.meta.env.DEV || !import.meta.env.PROD) }
  )

  useEffect(() => {
    const currentParams = new URLSearchParams(window.location.search)
    const isLogout =
      currentParams.get('action') === 'logout' ||
      currentParams.get('logout') === 'true' ||
      window.location.pathname === '/logout'

    if (isLogout) {
      logout()
      removeToken()
      auth.signOut().catch(() => {})
      const url = new URL(window.location.href)
      url.searchParams.delete('action')
      url.searchParams.delete('logout')
      const cleanPath = url.pathname === '/logout' ? '/' : url.pathname
      window.history.replaceState({}, '', cleanPath + (url.search ? url.search : ''))
      return
    }

    const validCookieToken = getToken()
    const reason = currentParams.get('reason')

    // If redirected due to expired token: hold the token and prompt OTP verification
    if (reason === 'token_expired' || reason === 'session_expired') {
      if (validCookieToken) {
        setExpiredToken(validCookieToken)
        requestExpiredTokenOtpMutation.mutateAsync({ token: validCookieToken })
          .then((res) => {
            setExpiredOtpSent(true)
            setExpiredMaskedPhone(res.maskedPhone)
            setExpiredRef(res.ref)
          })
          .catch((err) => {
            console.warn('[Login] Auto-request expired token OTP error:', err)
          })
      }
      return
    }

    if (reason === 'invalid_token') {
      if (user || validCookieToken) {
        logout()
        removeToken()
        auth.signOut().catch(() => {})
      }
      return
    }

    if (!validCookieToken) {
      if (user) {
        logout()
        removeToken()
        auth.signOut().catch(() => {})
      }
      return
    }

    if (user && validCookieToken) {
      let isSubscribed = true
      trpcClient.auth.verifyJWT
        .query({ token: validCookieToken })
        .then((result) => {
          if (!isSubscribed) return
          if (result?.userId) {
            if (redirectUrl) {
              try {
                const url = new URL(redirectUrl)
                url.searchParams.set('token', validCookieToken)
                url.searchParams.set('lng', i18n.language)
                window.location.href = url.toString()
              } catch {
                navigate('/profile')
              }
            } else {
              navigate('/profile')
            }
          } else {
            logout()
            removeToken()
            auth.signOut().catch(() => {})
          }
        })
        .catch((err) => {
          if (!isSubscribed) return
          console.warn('[Login] Existing token validation failed:', err)
          const errStr = String(err?.message || '')
          if (errStr.includes('expired') || errStr.includes('Expired')) {
            setExpiredToken(validCookieToken)
            requestExpiredTokenOtpMutation.mutateAsync({ token: validCookieToken })
              .then((res) => {
                setExpiredOtpSent(true)
                setExpiredMaskedPhone(res.maskedPhone)
                setExpiredRef(res.ref)
              })
              .catch(() => {})
          } else {
            logout()
            removeToken()
            auth.signOut().catch(() => {})
          }
        })

      return () => {
        isSubscribed = false
      }
    }
  }, [user, redirectUrl, navigate, i18n.language, logout])

  const handleLoginSuccess = (userData: any, token: string) => {
    setIsRedirecting(true)
    login(userData, token)

    if (redirectUrl) {
      try {
        const url = new URL(redirectUrl)
        url.searchParams.set('token', token)
        url.searchParams.set('lng', i18n.language)
        window.location.href = url.toString()
      } catch (e) {
        console.error('Invalid redirect URL', e)
        if (window.location.hostname === 'auth.jabeztech.com' || (window.location.hostname === 'localhost' && window.location.port === '4001')) {
          navigate('/profile')
        } else {
          navigate('/')
        }
      }
    } else {
      if (window.location.hostname === 'auth.jabeztech.com' || (window.location.hostname === 'localhost' && window.location.port === '4001')) {
        navigate('/profile')
      } else {
        navigate('/')
      }
    }
  }

  const handleGoogleSignIn = async () => {
    setIsLoading(true)
    setError('')
    try {
      provider.setCustomParameters({
        prompt: 'select_account',
      })

      const userCredential = await signInWithPopup(auth, provider)
      const idToken = await getIdToken(userCredential.user, true)

      const response = await loginWithFirebaseMutation.mutateAsync({ idToken })

      if (response?.data?.user) {
        handleLoginSuccess(response.data.user, response.data.token)
      } else {
        setError(t('login.errors.authFailed'))
      }
    } catch (error) {
      console.error('Google Sign-in error:', error)

      if (error instanceof TRPCClientError) {
        if (error.data?.code === 'NOT_FOUND') {
          setError(
            'User do not have account, please contact admin to create account first.'
          )
        } else {
          setError(error.message)
        }
      } else if (error instanceof Error) {
        if ((error as any).code === 'auth/popup-closed-by-user' || (error as any).code === 'auth/cancelled-popup-request') {
          setError('')
        } else {
          setError(error.message)
        }
      } else {
        setError('An unexpected error occurred')
      }
    } finally {
      if (!isRedirecting) {
        setIsLoading(false)
      }
    }
  }

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    try {
      const result = await loginWithPasswordMutation.mutateAsync({
        user: email,
        password: password,
      })

      if (result?.data?.user) {
        handleLoginSuccess(result.data.user, result.data.token)
        setError('')
      } else {
        setError(t('login.errors.invalidPassword'))
      }
    } catch (err) {
      console.error('Password verification error:', err)
      if (err instanceof TRPCClientError) {
        setError(err.message)
      } else {
        setError(t('login.errors.authFailed'))
      }
    } finally {
      if (!isRedirecting) {
        setIsLoading(false)
      }
    }
  }

  const handleVerifyExpiredTokenOtp = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!expiredToken || expiredOtp.length !== 6) return
    setIsLoading(true)
    setError('')
    try {
      const res = await verifyExpiredTokenOtpMutation.mutateAsync({
        token: expiredToken,
        code: expiredOtp,
        ref: expiredRef || undefined,
      })
      if (res?.data?.token && res?.data?.user) {
        handleLoginSuccess(res.data.user, res.data.token)
      } else {
        setError(t('verification.errors.invalidCode') || 'Invalid verification code')
      }
    } catch (err: any) {
      setError(err?.message || t('verification.errors.invalidCode') || 'Invalid verification code')
    } finally {
      if (!isRedirecting) {
        setIsLoading(false)
      }
    }
  }

  const handleResendExpiredOtp = async () => {
    if (!expiredToken) return
    setIsLoading(true)
    setError('')
    try {
      const res = await requestExpiredTokenOtpMutation.mutateAsync({ token: expiredToken })
      setExpiredOtpSent(true)
      setExpiredMaskedPhone(res.maskedPhone)
      setExpiredRef(res.ref)
    } catch (err: any) {
      setError(err?.message || 'Failed to resend code')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancelExpiredRecovery = () => {
    setExpiredToken(null)
    setExpiredOtp('')
    setExpiredOtpSent(false)
    logout()
    removeToken()
    auth.signOut().catch(() => {})
  }

  const handleSendMobileOtp = async (e?: React.FormEvent) => {
    if (e) e.preventDefault()
    if (!mobilePhone) return
    setIsLoading(true)
    setError('')
    try {
      const res = await requestMobileLoginOtpMutation.mutateAsync({ phone: mobilePhone })
      setMobileOtpSent(true)
      setMobileRef(res.ref)
    } catch (err: any) {
      setError(err?.message || 'Failed to send OTP')
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerifyMobileLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!mobilePhone || mobileOtp.length !== 6) return
    setIsLoading(true)
    setError('')
    try {
      const res = await verifyMobileLoginOtpMutation.mutateAsync({
        phone: mobilePhone,
        code: mobileOtp,
        ref: mobileRef || undefined,
      })
      if (res?.data?.token && res?.data?.user) {
        handleLoginSuccess(res.data.user, res.data.token)
      } else {
        setError(t('verification.errors.invalidCode') || 'Invalid verification code')
      }
    } catch (err: any) {
      setError(err?.message || t('verification.errors.invalidCode') || 'Invalid verification code')
    } finally {
      if (!isRedirecting) {
        setIsLoading(false)
      }
    }
  }

  const handleSignUp = () => {
    navigate('/signup')
  }

  return (
    <div className={`md:!bg-blue-200`}>
      <div
        className={`flex h-screen flex-col items-center justify-center overflow-auto`} // Changed from overflow-hidden to overflow-auto
      >
        {/* Login Card */}
        <div className='w-full max-w-md bg-white p-6 md:shadow-md md:rounded-lg'>
          <img
            src={logoImage}
            alt={t('login.logoAlt')}
            className='w-full h-35 md:h-40'
          />
          <div className='flex justify-between items-center align-middle pt-2 pb-4'>
            <div />
            <div className='flex justify-end'>
              <LanguageSwitcher />
            </div>
          </div>

          {/* Expired Token OTP Verification View */}
          {expiredToken ? (
            <div className='space-y-4'>
              <div className='p-3 bg-blue-50 border border-blue-200 rounded-lg text-center'>
                <h3 className='font-semibold text-base text-blue-900'>
                  {t('login.expiredSession.title') || 'Session Expired'}
                </h3>
                <p className='mt-1 text-xs text-blue-700'>
                  {t('login.expiredSession.instructions') || 'Your login session has expired. Please verify with OTP to renew your session.'}
                </p>
                {expiredMaskedPhone && (
                  <p className='mt-1 text-sm font-semibold text-blue-950'>
                    📱 {expiredMaskedPhone}
                  </p>
                )}
                {expiredOtpSent && (
                  <p className='mt-1 text-xs text-green-700 font-medium'>
                    ✓ {t('verification.messages.codeSent') || 'Verification code sent'}
                  </p>
                )}
              </div>

              {expiredRef && (
                <div className='py-2 px-3 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-between'>
                  <span className='text-xs font-medium text-gray-600'>
                    {t('verification.referenceCode') || 'SMS Reference Code'}:
                  </span>
                  <span className='font-mono font-bold text-sm text-blue-900 bg-white px-2.5 py-0.5 rounded border border-gray-200 shadow-sm'>
                    Ref: {expiredRef}
                  </span>
                </div>
              )}

              {/* Dev Mock SMS helper */}
              {mockSmsData && (
                <div className='p-2.5 bg-amber-50 border border-amber-300 rounded text-xs text-amber-900'>
                  <span>Dev Mock OTP: </span>
                  <button
                    type='button'
                    onClick={() => setExpiredOtp(mockSmsData.code)}
                    className='font-mono font-bold text-sm text-blue-700 underline ml-1'
                    title='Click to autofill'
                  >
                    {mockSmsData.code}
                  </button>
                  {mockSmsData.ref && (
                    <span className='ml-2 font-mono text-[11px] bg-amber-100 px-1 py-0.5 rounded'>
                      Ref: {mockSmsData.ref}
                    </span>
                  )}
                </div>
              )}

              <form onSubmit={handleVerifyExpiredTokenOtp} className='space-y-4'>
                <div>
                  <label htmlFor='expiredOtp' className='block text-sm font-medium text-gray-700'>
                    {t('verification.fields.code')}
                  </label>
                  <input
                    id='expiredOtp'
                    type='text'
                    maxLength={6}
                    value={expiredOtp}
                    onChange={(e) => setExpiredOtp(e.target.value)}
                    className='mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-center font-mono text-xl tracking-widest shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500'
                    placeholder='123456'
                    required
                    autoFocus
                  />
                </div>

                {error && <div className='text-sm text-red-600 text-center'>{error}</div>}

                <button
                  type='submit'
                  disabled={isLoading || expiredOtp.length !== 6}
                  className='w-full rounded-md bg-blue-600 py-2 px-4 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50'
                >
                  {isLoading
                    ? (t('login.buttons.signingIn') || 'Verifying...')
                    : (t('login.buttons.verifyAndSignIn') || 'Verify & Sign In')}
                </button>

                <div className='flex items-center justify-between text-xs pt-1'>
                  <button
                    type='button'
                    onClick={handleResendExpiredOtp}
                    disabled={isLoading}
                    className='text-blue-600 hover:text-blue-800 font-medium'
                  >
                    {t('verification.buttons.resendCode') || 'Resend Code'}
                  </button>
                  <button
                    type='button'
                    onClick={handleCancelExpiredRecovery}
                    className='text-gray-500 hover:text-gray-700'
                  >
                    {t('login.expiredSession.loginDifferentAccount') || 'Sign in with different account'}
                  </button>
                </div>
              </form>
            </div>
          ) : (
            /* Standard Login View */
            <>
              {/* Login Tabs: Email/Password vs Mobile Number */}
              <div className='flex border-b border-gray-200 mb-6'>
                <button
                  type='button'
                  onClick={() => {
                    setActiveTab('password');
                    setError('');
                  }}
                  className={`flex-1 py-2 font-medium text-sm text-center border-b-2 transition-colors ${
                    activeTab === 'password'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {t('login.tabs.email') || 'Email & Password'}
                </button>
                <button
                  type='button'
                  onClick={() => {
                    setActiveTab('mobile');
                    setError('');
                  }}
                  className={`flex-1 py-2 font-medium text-sm text-center border-b-2 transition-colors ${
                    activeTab === 'mobile'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {t('login.tabs.mobile') || 'Mobile Number'}
                </button>
              </div>

              {activeTab === 'password' ? (
                /* Email and Password Form */
                <form onSubmit={handleEmailLogin} className='space-y-4'>
                  <div>
                    <label
                      htmlFor='email'
                      className='block text-sm font-medium text-gray-700'
                    >
                      {t('login.fields.email')}
                    </label>
                    <input
                      id='email'
                      type='email'
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className='mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500'
                      placeholder={t('login.placeholders.email')}
                      required
                    />
                  </div>
                  <div>
                    <label
                      htmlFor='password'
                      className='block text-sm font-medium text-gray-700'
                    >
                      {t('login.fields.password')}
                    </label>
                    <input
                      id='password'
                      type='password'
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className='mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500'
                      placeholder={t('login.placeholders.password')}
                      required
                    />
                  </div>
                  {error && <div className='text-sm text-red-600'>{error}</div>}
                  <button
                    type='submit'
                    disabled={isLoading}
                    className='w-full rounded-md bg-blue-500 py-2 px-4 text-white hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50'
                  >
                    {isLoading
                      ? t('login.buttons.signingIn')
                      : t('login.buttons.signIn')}
                  </button>
                </form>
              ) : (
                /* Mobile Number Login Form */
                <div className='space-y-4'>
                  {!mobileOtpSent ? (
                    <form onSubmit={handleSendMobileOtp} className='space-y-4'>
                      <div>
                        <label htmlFor='loginMobilePhone' className='block text-sm font-medium text-gray-700'>
                          {t('signUp.fields.mobile')}
                        </label>
                        <div className='mt-1 relative rounded-md shadow-sm'>
                          <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                            <span className='text-gray-500 sm:text-sm font-medium'>🇹🇭 +66</span>
                          </div>
                          <input
                            id='loginMobilePhone'
                            type='tel'
                            value={mobilePhone}
                            onChange={(e) => setMobilePhone(e.target.value)}
                            className='block w-full pl-16 rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500'
                            placeholder='081-234-5678'
                            required
                          />
                        </div>
                        <p className='mt-1 text-xs text-gray-500'>
                          {t('signUp.messages.thaiPhoneNotice')}
                        </p>
                      </div>

                      {error && <div className='text-sm text-red-600'>{error}</div>}

                      <button
                        type='submit'
                        disabled={isLoading || !mobilePhone}
                        className='w-full rounded-md bg-blue-600 py-2 px-4 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50'
                      >
                        {isLoading
                          ? (t('login.buttons.signingIn') || 'Sending OTP...')
                          : (t('login.buttons.sendOtp') || 'Send OTP')}
                      </button>
                    </form>
                  ) : (
                    <form onSubmit={handleVerifyMobileLogin} className='space-y-4'>
                      {mobileRef && (
                        <div className='py-2 px-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between'>
                          <div className='text-xs text-blue-700'>
                            <span className='font-medium'>{t('verification.referenceCode') || 'SMS Reference Code'}:</span>
                            <span className='block text-[11px] text-blue-500 mt-0.5'>{t('verification.referenceNotice') || 'Match reference on incoming SMS'}</span>
                          </div>
                          <span className='font-mono font-bold text-sm text-blue-900 bg-white px-2.5 py-1 rounded border border-blue-200 shadow-sm'>
                            Ref: {mobileRef}
                          </span>
                        </div>
                      )}

                      {/* Dev Mock SMS quick-fill */}
                      {mockSmsData && (
                        <div className='p-2.5 bg-amber-50 border border-amber-300 rounded text-xs text-amber-900'>
                          <span>Dev Mock OTP: </span>
                          <button
                            type='button'
                            onClick={() => setMobileOtp(mockSmsData.code)}
                            className='font-mono font-bold text-sm text-blue-700 underline ml-1'
                            title='Click to autofill'
                          >
                            {mockSmsData.code}
                          </button>
                          {mockSmsData.ref && (
                            <span className='ml-2 font-mono text-[11px] bg-amber-100 px-1 py-0.5 rounded'>
                              Ref: {mockSmsData.ref}
                            </span>
                          )}
                        </div>
                      )}

                      <div>
                        <label htmlFor='loginMobileOtp' className='block text-sm font-medium text-gray-700'>
                          {t('verification.fields.code')}
                        </label>
                        <input
                          id='loginMobileOtp'
                          type='text'
                          maxLength={6}
                          value={mobileOtp}
                          onChange={(e) => setMobileOtp(e.target.value)}
                          className='mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-center font-mono text-xl tracking-widest shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500'
                          placeholder='123456'
                          required
                          autoFocus
                        />
                      </div>

                      {error && <div className='text-sm text-red-600 text-center'>{error}</div>}

                      <button
                        type='submit'
                        disabled={isLoading || mobileOtp.length !== 6}
                        className='w-full rounded-md bg-blue-600 py-2 px-4 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50'
                      >
                        {isLoading
                          ? (t('login.buttons.signingIn') || 'Verifying...')
                          : (t('login.buttons.verifyAndSignIn') || 'Verify & Sign In')}
                      </button>

                      <div className='flex items-center justify-between text-xs pt-1'>
                        <button
                          type='button'
                          onClick={handleSendMobileOtp}
                          disabled={isLoading}
                          className='text-blue-600 hover:text-blue-800 font-medium'
                        >
                          {t('verification.buttons.resendCode') || 'Resend Code'}
                        </button>
                        <button
                          type='button'
                          onClick={() => {
                            setMobileOtpSent(false);
                            setMobileOtp('');
                            setMobileRef('');
                          }}
                          className='text-gray-500 hover:text-gray-700'
                        >
                          {t('login.buttons.changePhone') || 'Change Number'}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              )}

              <div className='relative my-6'>
                <div className='absolute inset-0 flex items-center'>
                  <div className='w-full border-t border-gray-300' />
                </div>
                <div className='relative flex justify-center text-sm'>
                  <span className='bg-white px-2 text-gray-500'>
                    {t('login.messages.orContinueWith')}
                  </span>
                </div>
              </div>

              {/* Google Sign In */}
              <div className='mt-6'>
                <button
                  onClick={handleGoogleSignIn}
                  className='flex w-full items-center justify-center gap-3 rounded-md bg-gray-200 py-2 px-4 text-gray-700 shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                >
                  <img
                    src={googleIcon}
                    alt={t('login.googleIconAlt')}
                    className='h-5 w-5'
                  />
                  <span>{t('login.buttons.signInGoogle')}</span>
                </button>
              </div>

              {/* Sign Up Option */}
              <div className='mt-6 text-center'>
                <p className='text-sm text-gray-600'>
                  {t('login.messages.noAccount')}{' '}
                  <button
                    onClick={handleSignUp}
                    className='font-medium text-blue-600 hover:text-blue-500'
                  >
                    {t('login.buttons.signUp')}
                  </button>
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 999,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
        }}
        open={isLoading || isRedirecting}
      >
        <CircularProgress color='inherit' />
        <Typography variant='body1' component='div' sx={{ fontWeight: 500 }}>
          {isRedirecting
            ? (t('login.buttons.signingIn') || 'Signing in...')
            : (t('login.buttons.signingIn') || 'Signing in...')}
        </Typography>
      </Backdrop>
    </div>
  )
}

export default Login
